<template>
  <div class="from-price mb-4" :class="{ 'from-price--condensed': condensed }">
    <div class="from-price__price">
      <small v-if="condensed" class="from-price__from">
        {{ translate('from') }}
      </small>
      <div
        v-if="!condensed && stayPrice.agencyComission"
        class="from-price__travel-agency-commission"
      >
        <span>{{ stayPrice.agencyComission }}</span>
      </div>
      <div class="from-price__labels">
        <div v-if="!condensed && stayPrice.eCert" class="d-flex">
          <span class="from-price__e-cert-applied">
            <i class="icon-eCert xs color-warning"></i>
            {{ stayPrice.eCert }}
          </span>
        </div>
      </div>
      <Strikethrough
        v-if="stayPrice.discount"
        class="from-price__strikethrough"
        :stay="stay"
        :percentage-of-discount="stayPrice.discount.percentage"
        :currency="stayPrice.discount.currency"
        :amount="stayPrice.discount.amount"
        :showPercentage="!condensed"
        :isCrowdDiscount="isCrowdDiscount && hasCrowdLabel"
        :tabSelected="tabSelected"
        :localGroup="localGroup"
      />
      <div
        class="from-price__price-amount"
        :class="{ 'from-price__price-amount--discounted': stayPrice.discount }"
      >
        <div v-if="stayPrice.cash" class="from-price__cash">{{ stayPrice.cash }}</div>
        <div v-if="stayPrice.points" class="from-price__points">
          <span v-if="stayPrice.cash">+</span> {{ stayPrice.points }}
        </div>
        <small
          v-if="!condensed && stayPrice.perNight"
          label="rdNight"
          class="from-price__per-night"
        >
          {{ stayPrice.perNight }}
        </small>
      </div>
      <div class="from-price__lower-line">
        <div v-if="!condensed && stayPrice.cash">
          <small>
            <span
              v-if="stayPrice.taxes"
              :class="stayPrice.includesTaxes ? 'from-price__taxes' : 'from-price__taxes-excluded'"
            >
              {{ stayPrice.taxes }}
            </span>
          </small>
        </div>
      </div>
    </div>

    <div class="d-flex flex-column btn-custom" v-if="!condensed">
      <div class="from-price__button mt-2">
        <button
          class="btn cta-btn-custom"
          :class="{
            'm-progress': loading,
            'btn-color-warning': isDealOfTheDay,
            'btn-color-primary': !isDealOfTheDay
          }"
          :disabled="bookingDisabled"
          @click="buttonAction.onClick()"
        >
          <span class="button__text" v-if="!loading">
            {{ buttonText }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex'
import Stay from '@/multi-room-display/domain/Stay'
import Strikethrough from '../stay/Strikethrough'
import { CrowdTypes, isRoundedPriceEnabled } from '@/infrastructure/Configuration'
import { roundPrice } from '@/infrastructure/Utils'
import Room from '../../domain/Room'
const { mapGetters, mapActions, mapState } = createNamespacedHelpers('multiRoomDisplay')

export default {
  name: 'FromPriceButton',
  components: {
    Strikethrough
  },
  props: {
    stay: {
      type: Stay
    },
    isDealOfTheDay: {
      type: Boolean,
      defaultValue: false
    },
    condensed: {
      type: Boolean,
      default: false
    },
    hasCrowdLabel: {
      type: Boolean,
      defaultValue: false
    },
    tabSelected: {
      type: String
    },
    localGroup: {
      type: Object
    },
    isLastRoomTab: {
      type: Boolean
    },
    currentRoom: {
      type: Number
    },
    rooms: {
      type: Array
    },
    room: {
      type: Room
    }
  },
  data: () => ({
    crowdTypes: CrowdTypes,
    isProfessionalRateActive: global.ConfigDynamic.isEnabled(
      global.configDynamicKey.ROOMDISPLAY_PROFESSIONAL_RATE_ACTIVE
    ),
    isRoundedPriceEnabled: isRoundedPriceEnabled()
  }),
  computed: {
    ...mapGetters(['customer', 'bookingStayInProgress', 'loyaltyConsentAccepted', 'isUk']),
    ...mapState(['selectedId']),

    showNotTravelAgentModal() {
      return (
        ((this.customer.isLogged && !this.customer.isTravelAgent) || this.customer.isAnonymous) &&
        this.isProfessionalRateActive &&
        this.stay.professional
      )
    },
    loading() {
      return this.stay.isEqualsTo(this.bookingStayInProgress)
    },
    linkSize() {
      return this.condensed ? '1rem' : undefined
    },
    color() {
      return this.isDealOfTheDay ? '#E76B13' : undefined
    },
    bookingDisabled() {
      return this.stay.isNotEqualsTo(this.bookingStayInProgress) || this.isSelectedRoom(this.room)
    },
    buttonAction() {
      let button = {}

      if (this.isLastRoomTab) {
        if (this.customer.isLogged && !this.stay.canBook(this.customer)) {
          button = {
            label: 'rdMoreInfo',
            onClick: this.onShowInsufficientPointsModal
          }
        } else if (this.showNotTravelAgentModal) {
          this.$root.$emit('bv::show::modal', 'not-travel-agent-modal')
        } else {
          button = {
            label: 'rdBookNow',
            onClick: this.onBookNow
          }
        }
      } else {
        button = {
          label: 'rdSelect',
          onClick: () => {
            this.$emit(
              'roomSelected',
              true,
              this.stay.rates,
              this.stay.roomId,
              this.stay.mealPlanDto.code,
              this.stay.code,
              this.stay.mealPlanDto.description,
              this.stay.name,
              this.stay.dealOfTheDay !== undefined ? this.stay.dealOfTheDay : null
            )
          }
        }
      }

      return button
    },
    stayPrice() {
      const priceObject = {}
      const stay = this.stay
      const translate = this.translate
      const isUk = this.isUk

      if (stay.comesFromTravelAgency) {
        priceObject.agencyComission = stay.cash.hasTravelAgencyCommissionPercentage()
          ? translate(
            'rdPercentCommission',
            undefined,
            stay.cash.travelAgencyCommissionPercentage.toFixed(2)
          )
          : translate('rdNoCommission')
      }

      if (stay.eCertIsApplied) {
        priceObject.eCert = translate('rdEcertApplied')
      }

      if (stay.cash && stay.cash.formattedAverageTaxAndFee) {
        priceObject.formattedAverageTaxAndFee = translate(
          'boExcludedTaxesAndFees',
          '{0}',
          stay.cash.formattedAverageTaxAndFee
        )
      }

      if (!isUk && stay.hasDiscount()) {
        if (stay.cash.hasDiscount()) {
          priceObject.discount = {
            amount: String(stay.cash.stayCashAmount().toFixed(2)),
            percentage: stay.cash.percentageOfDiscountPerNight,
            currency: stay.cash.currency
          }
        }

        if (stay.points.hasDiscount()) {
          priceObject.discount = {
            amount: translate(
              'boNumPoints',
              stay.points.undiscountedAmountPerNight,
              stay.points.undiscountedAmountPerNight
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            ),
            percentage: stay.points.percentageOfDiscountPerNight
          }
        }
      }
      if (stay.cash.hasCash()) {
        if (stay.isAccumulated()) {
          this.isRoundedPriceEnabled
            ? (priceObject.cash = `${stay.cash.currency} ${roundPrice(stay.cash.amountAccumulatedPerNights)}`)
            : (priceObject.cash = stay.cash.formattedAccumulatedAmountPerNight)
        } else {
          this.isRoundedPriceEnabled
            ? (priceObject.cash = `${stay.cash.currency} ${roundPrice(stay.cash.baseAmountPerNight)}`)
            : (priceObject.cash = stay.cash.formattedAmountPerNight)
        }
      }

      if (stay.points.hasPoints()) {
        if (stay.points.isAccumulated()) {
          priceObject.points = translate(
            'boNumPoints',
            stay.points.totalAmountForAllNights,
            stay.points.totalAmountForAllNights.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          )
        } else {
          priceObject.points = translate(
            'boNumPoints',
            stay.points.amountPerNight,
            stay.points.amountPerNight.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          )
        }
      }
      if (stay.isAccumulated()) {
        priceObject.perNight = `${stay.cash.accumulated.labels.for} ${stay.cash.nights} ${stay.cash.accumulated.labels.nights}`
      } else priceObject.perNight = translate('rdNight')
      if (!stay.includesTaxes && stay.cash.hasTaxes()) {
        if (stay.isAccumulated()) {
          priceObject.taxes = translate('rdTaxExcluded')
        } else {
          priceObject.taxes = translate('rdTaxExcluded')
        }
      } else if (stay.includesTaxes) {
        priceObject.taxes = translate('rdUKTaxIncluded')
      }
      return priceObject
    },
    isCrowdDiscount() {
      return (
        this.customer.crowd === CrowdTypes.DISCOUNT &&
        this.stay.hasDiscount() &&
        this.stay.crowdDiscount
      ) // && this.stayPrice.discount.percentage < 21 -> it will be discomented when TMS send us crowdDiscounts in stays with less than 20% discount
    },
    isProfessionalRate() {
      return (
        this.isProfessionalRateActive &&
        this.translate('rdProfessionalRateApplied') &&
        this.stay.professional
      )
    },
    buttonText() {
      if (this.isSelectedRoom(this.room)) {
        return this.translate('rdSelectedRoom')
      }
      return this.isLastRoomTab ? this.translate('rdBookNow') : this.translate('rdSelect')
    }
  },
  methods: {
    ...mapActions(['preBooking']),
    onShowInsufficientPointsModal() {
      this.$root.$emit('bv::show::modal', 'InsufficientPointsModal')
    },
    async onBookNow() {
      this.$emit(
        'roomSelected',
        true,
        this.stay.rates,
        this.stay.roomId,
        this.stay.mealPlanDto.code,
        this.stay.code,
        this.stay.mealPlanDto.description,
        this.stay.name,
        this.stay.dealOfTheDay !== undefined ? this.stay.dealOfTheDay : null
      )
      if (this.customer.isAnonymous && this.stay.points.hasPoints()) {
        this.$root.$emit('bv::show::modal', 'NonLoggedModal')
      } else {
        if (this.stay.memberOnly) {
          global.SessionStorage.set(global.sessionStorageDictionary.MOR, true)
          global.SessionStorage.set(global.sessionStorageDictionary.externalBrandPreference, 'rhg')
          global.SessionStorage.set(global.sessionStorageDictionary.brandPreference, 'rhg')
        } else {
          global.SessionStorage.remove(global.sessionStorageDictionary.MOR)
        }
        this.preBooking({ stay: this.stay, rooms: this.rooms })
      }
    },
    isSelectedRoom(room) {
      const currentIndex = this.currentRoom - 1
      if (currentIndex === 0) {
        return this.stay.id === this.selectedId
      } else {
        return this.rooms[currentIndex] && this.rooms[currentIndex].code === room.id
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.from-price {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  @media (max-width: 992px) {
    align-items: flex-start;
    width: 100%;
    .btn-custom {
      width: 100%;
      .from-price__button {
        .cta-btn-custom {
          width: 100%;
        }
      }
    }
  }
  .from-price__price {
    justify-content: flex-end;
    margin-bottom: 5px;
  }
  .from-price__button {
    .cta-btn-custom {
      padding: 1.6rem 2.4rem;
      line-height: 2rem;
      height: 48px;
      width: 221px;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: row;
      gap: 10px;
      border-radius: 48px;

      &.btn-color-primary {
        background: #c20808;
      }
      &.btn-color-warning {
        background: #e17e45;
      }
      .button__text {
        font-style: normal;
        font-weight: 700;
        font-size: 1.5rem;
        line-height: 22px;
        text-align: center;
        text-transform: uppercase;
        color: #ffffff;
        flex: none;
        order: 0;
        flex-grow: 0;
      }
    }
  }
  &__separator {
    @media (min-width: 1024px) {
      border-right: solid 1px #d8d8d8;
      height: 20px;
      margin: 0 10px;
    }
  }
  &__price {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    @media (max-width: 992px) {
      align-items: flex-start;
    }
  }

  &__labels {
    display: flex;
    flex-direction: column;
    @media (min-width: 1024px) {
      flex-direction: row;
    }
  }

  &__travel-agency-commission {
    font-size: 1.2rem;
    font-style: italic;
  }

  &__crowd-label {
    align-items: center;
    display: flex;
    order: 10;
    @media (min-width: 1024px) {
      order: 0;
    }

    &--text {
      color: #382246;
      font-size: 1.15rem;
      font-weight: bold;
    }
  }

  &__price-amount {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;

    &--discounted {
      .from-price__cash {
        color: black;
      }

      .from-price__points {
        color: crimson;

        @media (min-width: 992px) {
          align-self: flex-end;
        }
      }
    }
  }
  &__cash,
  &__points {
    font-size: 1.8rem;
    color: black;
    font-weight: bold;
    margin-right: 0.4rem;
  }

  &__points {
    white-space: nowrap;
  }

  &__per-night {
    font-size: 1.4rem;
  }

  &__lower-line {
    display: flex;
    flex-flow: wrap;
    align-items: baseline;
    margin-top: 0.4rem;
  }

  &__taxes {
    display: block;
    font-size: 1.4rem;
    line-height: 1;
  }

  &__taxes-excluded {
    display: block;
    line-height: 1;
  }

  &__wrap-discount {
    @media (min-width: 768px) {
      display: flex;
      flex-direction: row-reverse;
      align-items: flex-start;
    }
  }

  &--condensed {
    * {
      margin: 0;
    }

    @media (min-width: 992px) {
      flex-direction: column;
    }

    .promocode {
      width: 100%;
    }

    .from-price {
      &__price {
        text-align: center;

        @media (min-width: 992px) {
          flex-wrap: wrap;
          flex-direction: row;
          align-items: end;
          justify-content: flex-end;
        }
      }

      &__cash,
      &__points {
        font-size: 1.4rem;
        color: black;
      }

      &__lower-line {
        order: 0;
        margin-bottom: 1.2rem;
        justify-content: center;

        @media (min-width: 992px) {
          order: 4;
          flex: 1 1 auto;
          width: 100%;
          justify-content: flex-end;
          margin-bottom: 0;
        }
      }

      &__from {
        order: 1;

        @media (min-width: 992px) {
          margin-bottom: 0.5rem;
          margin-right: 0.4rem;
        }
      }

      &__price-amount {
        order: 2;
        flex-direction: column;
        align-items: center;

        @media (min-width: 992px) {
          align-items: flex-end;
        }
      }

      &__strikethrough {
        order: 3;
        justify-content: center;

        @media (min-width: 992px) {
          order: 0;
          flex: 1 1 auto;
          width: 100%;
          justify-content: flex-end;
        }
      }
    }
  }
}
</style>
